import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { message, Modal, notification, Select, Skeleton, Spin, Tag, Tooltip } from 'antd';
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import { useRouter } from '../../../core/hooks/useRouter';
import { projectService, ProjectExecution, ProjectSummary, CalcJob, CalcResultSummary, CalculationResult } from './ProjectService';
import { k1Service, PartnerK1Summary } from '../K1Service';
import { CreateProjectModal } from '../../ProjectView/pages/ProjectList/CreateProjectModal';
import styles from './Project.module.css';

// ── Status helpers ──────────────────────────────────────────────────────────────

function StatusIcon({ status, errorMessage }: { status: CalcJob['status'] | string; errorMessage?: string | null }) {
  if (status === 'Completed') {
    return (
      <span className={styles.iconCompleted}>
        <CheckOutlined style={{ fontSize: 12 }} />
      </span>
    );
  }
  if (status === 'Running' || status === 'InProgress') {
    return (
      <span className={styles.iconInProgress}>
        <LoadingOutlined style={{ fontSize: 12 }} />
      </span>
    );
  }
  if (status === 'Failed' || status === 'Error') {
    const icon = (
      <span className={styles.iconError}>
        <CloseOutlined style={{ fontSize: 12 }} />
      </span>
    );
    return errorMessage ? <Tooltip title={errorMessage}>{icon}</Tooltip> : icon;
  }
  if (status === 'Queued') {
    return (
      <span className={styles.iconPending}>
        <ClockCircleOutlined style={{ fontSize: 13 }} />
      </span>
    );
  }
  return (
    <span className={styles.iconPending}>
      <MinusOutlined style={{ fontSize: 13 }} />
    </span>
  );
}

// ── FIX 1: Job label with tier-based fallback ────────────────────────────────

const TIER_LABELS: Record<number, string> = {
  1: 'Taxable Income',
  2: 'Income Allocation',
  3: 'Capital Account Roll-forward',
  4: 'Period Depreciation (MACRS)',
  5: 'Debt Allocation',
  6: 'State Apportionment',
  7: 'Contribution Event',
};

function isRealFallback(val: string | null | undefined): boolean {
  if (!val?.trim()) return true;
  const v = val.trim().toLowerCase();
  return v === 'real' || v.startsWith('real ') || v === 'undefined' || v === 'null';
}

function getJobLabel(job: CalcJob, index: number): string {
  console.log('job fields:', job.calcName, job.standardCalculationId,
    job.sequence, (job as unknown as Record<string, unknown>).tierSequence, (job as unknown as Record<string, unknown>).jobType, job.type);
  if (!isRealFallback(job.calcName)) return job.calcName!.trim();
  if (!isRealFallback(job.standardCalculationId)) return job.standardCalculationId!.trim();
  return TIER_LABELS[index + 1] ?? `Step ${index + 1}`;
}

// ── Main component ─────────────────────────────────────────────────────────────

export const Project: React.FC = () => {
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const { selectedPartnership, taxYear } = usePartnershipContext();
  const { go } = useRouter();

  const [project, setProject] = useState<ProjectExecution | null>(null);
  const [allProjects, setAllProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [noProject, setNoProject] = useState(false);
  const [activeTab, setActiveTab] = useState<'calculations' | 'results' | 'workspace'>('calculations');
  const [running, setRunning] = useState(false);
  const [polling, setPolling] = useState(false);
  const [tabFading, setTabFading] = useState(false);
  const [results, setResults] = useState<CalcResultSummary[]>([]);
  const [detailedResults, setDetailedResults] = useState<CalculationResult[]>([]);
  const [partnerList, setPartnerList] = useState<PartnerK1Summary[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const lastPartnershipIdRef = useRef<string | undefined>(undefined);

  const fetchAllProjects = useCallback(async (partnershipId: string, year: string) => {
    try {
      const list = await projectService.listByPartnership(partnershipId, year);
      setAllProjects(list);
    } catch {
      setAllProjects([]);
    }
  }, []);

  useEffect(() => {
    if (urlProjectId) {
      const currentId = selectedPartnership?.id;
      if (lastPartnershipIdRef.current !== undefined && lastPartnershipIdRef.current !== currentId) {
        lastPartnershipIdRef.current = currentId;
        go('/calculations/project');
        return;
      }
      lastPartnershipIdRef.current = currentId;
      setLoading(true);
      setNoProject(false);
      projectService.getStatus(urlProjectId)
        .then((data) => setProject(data))
        .catch((err: unknown) => {
          const status = (err as { status?: number })?.status;
          if (status === 404) setNoProject(true);
          else message.error('Failed to load project');
          setProject(null);
        })
        .finally(() => setLoading(false));
      if (selectedPartnership) {
        fetchAllProjects(selectedPartnership.id, taxYear);
      }
    } else if (selectedPartnership) {
      // List mode: show all projects as cards, don't auto-load one
      setProject(null);
      setNoProject(false);
      setLoading(true);
      projectService.listByPartnership(selectedPartnership.id, taxYear)
        .then((list) => setAllProjects(list))
        .catch(() => setAllProjects([]))
        .finally(() => setLoading(false));
    } else {
      setProject(null);
      setNoProject(false);
      setAllProjects([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProjectId, selectedPartnership, taxYear, fetchAllProjects]);

  useEffect(() => {
    if (!project) { setResults([]); setDetailedResults([]); setPartnerList([]); return; }
    setResultsLoading(true);
    Promise.all([
      projectService.getResults(project.id),
      k1Service.getPartnerList(project.id).catch((): PartnerK1Summary[] => []),
    ])
      .then(async ([summaries, partners]) => {
        setResults(summaries);
        setPartnerList(partners);
        // Fetch only one completed job's results for K-1 line breakdown
        // (fetching all jobs inflates totals by summing across duplicate rows)
        const firstCompletedJob = project.jobs?.find(j => j.status === 'Completed');
        if (firstCompletedJob) {
          const lines = await projectService.getJobResults(firstCompletedJob.id).catch(() => []);
          setDetailedResults(lines);
        } else {
          setDetailedResults([]);
        }
      })
      .catch(() => { setResults([]); setDetailedResults([]); setPartnerList([]); })
      .finally(() => setResultsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // ── Polling ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!polling || !project) return;
    const interval = setInterval(async () => {
      try {
        const updated = await projectService.getStatus(project.id);
        setProject(updated);
        const allDone = (updated.jobs ?? []).every(
          (j) => j.status === 'Completed' || j.status === 'Failed' || j.status === 'Error',
        );
        if (allDone) {
          setPolling(false);
          setRunning(false);
          clearInterval(interval);
          message.success('All calculations complete');
        }
      } catch {
        // silence network errors during polling
      }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, project?.id]);

  // ── Run calculation ───────────────────────────────────────────────────────────

  const handleRunCalculation = async () => {
    if (!project) return;
    setRunning(true);
    try {
      await projectService.runNext(project.id);
      setPolling(true);
    } catch (err: unknown) {
      const errData = (err as { data?: { message?: string; title?: string } })?.data;
      notification.error({
        message: 'Failed to start calculation run',
        description: errData?.message ?? errData?.title ?? 'An unexpected error occurred.',
      });
      setRunning(false);
    }
  };

  // FIX 3: switch to a different project by id
  const handleSelectProject = async (projectId: number) => {
    if (projectId === Number(project?.id)) return;
    setLoading(true);
    try {
      const data = await projectService.getStatus(projectId);
      setProject(data);
    } catch {
      message.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = () => {
    if (!project) return;
    Modal.confirm({
      title: `Delete project "${project.name}"?`,
      content: 'This cannot be undone. The project will be archived and removed from this view.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeleting(true);
        try {
          await projectService.archiveProject(project.id);
          message.success('Project deleted');
          go('/calculations/project');
        } catch {
          message.error('Failed to delete project');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleDeleteFromList = (p: { id: number; name: string }) => {
    Modal.confirm({
      title: `Delete "${p.name}"?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await projectService.archiveProject(p.id);
          message.success('Project deleted');
          if (selectedPartnership) {
            fetchAllProjects(selectedPartnership.id, taxYear);
          }
        } catch {
          message.error('Failed to delete project');
        }
      },
    });
  };

  const switchTab = (tab: 'calculations' | 'results') => {
    if (tab === activeTab) return;
    setTabFading(true);
    setTimeout(() => {
      setActiveTab(tab);
      setTabFading(false);
    }, 150);
  };

  const jobs = project?.jobs ?? [];
  const hasPending = jobs.some((j) => j.status === 'Pending' || j.status === 'Queued');

  // ── Render helpers ────────────────────────────────────────────────────────────

  const renderProjectList = () => {
    if (loading) {
      return (
        <div className={styles.loadingState}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      );
    }

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: 0 }}>Projects</h1>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, margin: '4px 0 0' }}>
              {selectedPartnership?.name} · {taxYear}
            </p>
          </div>
          <button className={styles.btnRun} onClick={() => setShowCreate(true)}>
            <PlusOutlined style={{ fontSize: 11 }} /> New Project
          </button>
        </div>

        {allProjects.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📁</div>
            <div className={styles.emptyText}>No calculation projects for this partnership and tax year.</div>
            <button className={styles.btnRun} onClick={() => setShowCreate(true)} style={{ marginTop: 16 }}>
              <PlusOutlined style={{ fontSize: 11 }} /> Create Project
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {allProjects.map((p) => {
              const statusColor = p.status === 'Completed' ? '#22c55e'
                : p.status === 'Archived' ? '#94a3b8'
                : '#f26522';
              return (
                <div
                  key={p.id}
                  style={{
                    background: 'rgba(26,39,68,0.82)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 12,
                    padding: '20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: '1.3' }}>{p.name}</div>
                    <Tag style={{
                      fontSize: 10,
                      fontWeight: 600,
                      background: `${statusColor}22`,
                      border: `1px solid ${statusColor}55`,
                      color: statusColor,
                      borderRadius: 4,
                      padding: '1px 7px',
                      margin: 0,
                      flexShrink: 0,
                    }}>
                      {p.status || 'Active'}
                    </Tag>
                  </div>

                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                    {p.taxYear ? `FY${p.taxYear}` : taxYear}
                    {p.dateCreated && (
                      <>
                        <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
                        Created {new Date(p.dateCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button
                      onClick={() => go(`/calculations/project/${p.id}`)}
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        background: '#f26522',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 7,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Open
                    </button>
                    <button
                      onClick={() => handleDeleteFromList(p)}
                      style={{
                        padding: '8px 14px',
                        background: 'transparent',
                        color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.35)',
                        borderRadius: 7,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <DeleteOutlined style={{ fontSize: 12 }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const renderCalculationsTab = () => {
    if (loading) {
      return (
        <div className={styles.loadingState}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      );
    }

    if (!project || jobs.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>⚙️</div>
          <div className={styles.emptyText}>No calculations configured for this project.</div>
        </div>
      );
    }

    const pct = Math.round(project.progressPct ?? 0);
    const waiting = (project.totalCalcs ?? 0) - (project.completedCalcs ?? 0);

    return (
      <>
        <div className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <span className={styles.progressLabel}>Calculation Sequence</span>
            {/* FIX 4: "X of Y complete · Z waiting" */}
            <span className={styles.progressCount}>
              {project.completedCalcs} of {project.totalCalcs} complete · {waiting} waiting
            </span>
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className={styles.sequenceList}>
          {jobs
            .slice()
            .sort((a, b) => a.sequence - b.sequence)
            .map((job, i) => (
              <div
                key={job.id}
                className={job.status === 'InProgress' ? styles.jobItemInProgress : styles.jobItem}
                style={{ cursor: 'pointer' }}
                onClick={() => go(`/calculations/project/${project.id}/workspace?calc=${encodeURIComponent(getJobLabel(job, i))}`)}
              >
                <span className={styles.seqNum}>{job.sequence + 1}</span>
                <StatusIcon status={job.status} errorMessage={job.errorMessage} />
                <div className={styles.jobInfo}>
                  {/* FIX 1: tier-based fallback label */}
                  <div className={styles.jobName}>{getJobLabel(job, i)}</div>
                  <div className={styles.jobMeta}>
                    {job.standardCalculationId && (
                      <span>{job.standardCalculationId}</span>
                    )}
                  </div>
                </div>
                <div>
                  {job.status === 'Completed' && job.resultSummary ? (
                    <span className={styles.jobResult}>
                      {(() => {
                        try {
                          const p = JSON.parse(job.resultSummary as string);
                          if (p?.ResultCount != null) {
                            return (
                              <>
                                {Number(p.ResultCount).toLocaleString()} rows
                                {p.CompletedAt && (
                                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.50)' }}>
                                    {' · '}{new Date(p.CompletedAt as string).toLocaleDateString()}
                                  </span>
                                )}
                              </>
                            );
                          }
                        } catch { /* plain string */ }
                        return job.resultSummary;
                      })()}
                    </span>
                  ) : job.status === 'Running' || job.status === 'InProgress' ? (
                    <span className={styles.jobResultRunning}>Running...</span>
                  ) : job.status === 'Failed' || job.status === 'Error' ? (
                    <span className={styles.jobResult} style={{ color: '#ef4444' }}>Failed</span>
                  ) : job.status === 'Queued' ? (
                    <span className={styles.jobResultMuted}>Waiting</span>
                  ) : (
                    <span className={styles.jobResultMuted}>—</span>
                  )}
                </div>
              </div>
            ))}
        </div>
      </>
    );
  };

  const renderResultsTab = () => {
    if (loading || resultsLoading) {
      return (
        <div className={styles.loadingState}>
          <Spin />
        </div>
      );
    }

    const completedJobs = (project?.jobs ?? []).filter((j) => j.status === 'Completed');

    if (completedJobs.length === 0 && results.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><BarChartOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyText}>
            Run calculations to see results here.
          </div>
        </div>
      );
    }

    const totalOutputRows = results.reduce((a, r) => a + (r.outputRows ?? 0), 0);
    const partnerCount = partnerList.length > 0
      ? partnerList.length
      : new Set(detailedResults.map(r => r.partnerId)).size;
    const totalIncome = partnerList.length > 0
      ? partnerList.reduce((sum, p) => sum + (p.incomeAllocated || 0), 0)
      : null;

    const sumByLineCode = (lineCode: string) =>
      detailedResults
        .filter(r => r.lineCode === lineCode)
        .reduce((a, r) => a + r.amount, 0);

    const l1 = sumByLineCode('K1_L1');
    const l5 = sumByLineCode('K1_L5');
    const l9a = sumByLineCode('K1_L9A');
    const l13 = sumByLineCode('K1_L13');
    const l16 = sumByLineCode('K1_L16');
    const l20 = sumByLineCode('K1_L20');

    const taxYearDisplay = project?.taxYear
      ? `FY${String(project.taxYear).replace(/^FY/i, '')}`
      : '—';

    const hasLineData = detailedResults.length > 0;
    const formatCurrency = (amt: number | null) => {
      if (amt === null || (!hasLineData && partnerList.length === 0)) return '—';
      const absAmt = Math.abs(amt);
      const sign = amt < 0 ? '-' : '';
      if (absAmt >= 1_000_000_000) return `${sign}$${(absAmt / 1_000_000_000).toFixed(1)}B`;
      if (absAmt >= 1_000_000) return `${sign}$${(absAmt / 1_000_000).toFixed(1)}M`;
      if (absAmt >= 1_000) return `${sign}$${(absAmt / 1_000).toFixed(1)}K`;
      return `${sign}$${amt.toFixed(0)}`;
    };

    return (
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {/* Summary card */}
        <div style={{
          background: 'rgba(26,39,68,0.82)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          padding: 24,
          marginBottom: 16,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                {project?.name ?? '—'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                {project?.completedCalcs ?? completedJobs.length} calculations completed
              </div>
            </div>
            <div style={{
              background: 'rgba(34,197,94,0.15)',
              border: '1px solid rgba(34,197,94,0.30)',
              borderRadius: 6,
              padding: '4px 12px',
              fontSize: 12,
              fontWeight: 600,
              color: '#22c55e',
            }}>
              All Complete ✓
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
            {[
              { label: 'Output Rows', value: totalOutputRows > 0 ? totalOutputRows.toLocaleString() : '—', color: '#fff' },
              { label: 'Partners', value: partnerCount > 0 ? String(partnerCount) : '—', color: '#fff' },
              { label: 'Income Allocated', value: formatCurrency(totalIncome ?? l1), color: '#F26522' },
              { label: 'Tax Year', value: taxYearDisplay, color: '#fff' },
            ].map(metric => (
              <div key={metric.label} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.40)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 6,
                }}>
                  {metric.label}
                </div>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: metric.color,
                }}>
                  {metric.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Generate K-1s CTA — above K-1 summary so it's always visible */}
        <div style={{ textAlign: 'right', marginBottom: 24 }}>
          <button
            onClick={() => go('/tax-form')}
            style={{
              background: '#F26522',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 28px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {partnerCount > 0 ? `Generate ${partnerCount} K-1s →` : 'Generate K-1s →'}
          </button>
        </div>

        {/* K-1 lines summary */}
        <div style={{
          background: 'rgba(26,39,68,0.82)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
        }}>
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
            marginBottom: 16,
          }}>
            K-1 Allocation Summary
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[
              { line: 'Line 1', label: 'Ordinary Business Income', value: formatCurrency(l1) },
              { line: 'Line 5', label: 'Interest Income', value: formatCurrency(l5) },
              { line: 'Line 9a', label: 'Net LT Capital Gain', value: formatCurrency(l9a) },
              { line: 'Line 13', label: 'Depreciation', value: formatCurrency(l13) },
              { line: 'Line 16', label: 'Foreign Tax Credit', value: formatCurrency(l16) },
              { line: 'Line 20', label: 'Ending Capital', value: formatCurrency(l20) },
            ].map(item => (
              <div key={item.line} style={{
                background: 'rgba(10,10,20,0.40)',
                borderRadius: 8,
                padding: '12px 16px',
              }}>
                <div style={{
                  fontSize: 10,
                  color: '#F26522',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}>
                  {item.line}
                </div>
                <div style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.45)',
                  marginTop: 2,
                  marginBottom: 6,
                }}>
                  {item.label}
                </div>
                <div style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: item.value.startsWith('-') ? '#f87171' : '#fff',
                }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Root render ───────────────────────────────────────────────────────────────

  return (
    <div className={styles.pageWrap}>
      {!selectedPartnership ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🧮</div>
          <div className={styles.emptyText}>
            Select a client and partnership to view calculations.
          </div>
        </div>
      ) : !urlProjectId ? (
        renderProjectList()
      ) : noProject ? (
        <>
          <button className={styles.backLink} onClick={() => go('/calculations/project')}>
            <ArrowLeftOutlined style={{ fontSize: 11 }} />
            Back to Projects
          </button>
          <div className={styles.projectHeader}>
            <div className={styles.projectTitleBlock}>
              <div className={styles.projectTitleRow}>
                <h1 className={styles.projectName}>Project Not Found</h1>
              </div>
              <p className={styles.projectMeta}>
                {selectedPartnership.name}
                <span className={styles.metaDot}>·</span>
                {taxYear}
              </p>
            </div>
          </div>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📁</div>
            <div className={styles.emptyText}>This project could not be found.</div>
          </div>
        </>
      ) : (
        <>
          <button className={styles.backLink} onClick={() => go('/calculations/project')}>
            <ArrowLeftOutlined style={{ fontSize: 11 }} />
            Back to Projects
          </button>

          {/* FIX 1: Project selector — Ant Design Select, prominent styling */}
          {allProjects.length > 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16,
              padding: '10px 14px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 10,
            }}>
              <span style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.45)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                whiteSpace: 'nowrap',
                minWidth: 56,
              }}>
                PROJECT
              </span>
              <Select
                value={Number(project?.id)}
                onChange={(val) => handleSelectProject(Number(val))}
                style={{ flex: 1, minWidth: 300 }}
                popupClassName="project-selector-popup"
                styles={{
                  popup: {
                    root: {
                      background: '#1a1a2e',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8,
                    },
                  },
                }}
                variant="outlined"
                optionRender={(opt) => {
                  const p = allProjects.find((x) => x.id === Number(opt.value));
                  if (!p) return <span>{opt.label}</span>;
                  const statusColor = p.status === 'Completed' ? '#22c55e'
                    : p.status === 'Archived' ? '#94a3b8'
                    : '#f26522';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ fontWeight: 500, color: '#fff' }}>{p.name}</span>
                      {p.status && (
                        <Tag
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            background: `${statusColor}22`,
                            border: `1px solid ${statusColor}55`,
                            color: statusColor,
                            borderRadius: 4,
                            padding: '1px 7px',
                            margin: 0,
                          }}
                        >
                          {p.status}
                        </Tag>
                      )}
                    </div>
                  );
                }}
                options={allProjects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
          )}

          {/* Project header */}
          <div className={styles.projectHeader}>
            <div className={styles.projectTitleBlock}>
              <div className={styles.projectTitleRow}>
                <h1 className={styles.projectName}>{project?.name ?? '—'}</h1>
                {project && (() => {
                  const completedCount = project.completedCalcs ?? 0;
                  const totalCount = project.totalCalcs ?? 0;
                  const allComplete = totalCount > 0 && completedCount >= totalCount;
                  if (allComplete) {
                    return <span className={styles.badgeCompleted}>All Complete</span>;
                  }
                  return (
                    <span className={styles.badgeInProgress}>
                      {completedCount} of {totalCount} Complete
                    </span>
                  );
                })()}
              </div>
              {project && (
                <p className={styles.projectMeta}>
                  PRJ-{String(project.id).slice(0, 8).toUpperCase()}
                  <span className={styles.metaDot}>·</span>
                  {project.partnershipName}
                  <span className={styles.metaDot}>·</span>
                  {project.engagementName}
                  <span className={styles.metaDot}>·</span>
                  Tax Year {project.taxYear}
                </p>
              )}
            </div>

            {/* FIX 2: action buttons row — Delete + New Project + Run remaining */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 38,
                  padding: '0 14px',
                  background: 'transparent',
                  border: '1px solid rgba(239,68,68,0.35)',
                  borderRadius: 7,
                  color: '#ef4444',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s, border-color 0.15s',
                  opacity: deleting ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.08)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                onClick={handleDeleteProject}
                disabled={deleting}
              >
                <DeleteOutlined style={{ fontSize: 12 }} />
                Delete
              </button>

              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 38,
                  padding: '0 16px',
                  background: 'transparent',
                  border: '1px solid rgba(242,101,34,0.45)',
                  borderRadius: 7,
                  color: '#f26522',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(242,101,34,0.08)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                onClick={() => setShowCreate(true)}
              >
                <PlusOutlined style={{ fontSize: 11 }} />
                New Project
              </button>

              <button
                className={styles.btnRun}
                onClick={handleRunCalculation}
                disabled={running || !hasPending || loading}
              >
                {running ? (
                  <>
                    <Spin size="small" />
                    Running...
                  </>
                ) : (
                  <>
                    <PlayCircleOutlined />
                    Run Calculation
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className={styles.tabBar}>
            <button
              className={activeTab === 'calculations' ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => switchTab('calculations')}
            >
              Calculations
            </button>
            <button
              className={activeTab === 'results' ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => switchTab('results')}
            >
              Results
            </button>
            <button
              className={styles.tabBtn}
              onClick={() => project && go(`/calculations/project/${project.id}/workspace`)}
            >
              Workspace
            </button>
          </div>

          <div
            className={styles.tabContent}
            style={{ opacity: tabFading ? 0 : 1, transition: 'opacity 0.15s ease-in-out' }}
          >
            {activeTab === 'calculations' ? renderCalculationsTab() : activeTab === 'results' ? renderResultsTab() : null}
          </div>
        </>
      )}

      {/* FIX 2: Create Project modal */}
      <CreateProjectModal
        visible={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(projectId) => {
          setShowCreate(false);
          go(`/calculations/project/${projectId}/workspace`);
        }}
      />
    </div>
  );
};
